import { useEffect, useMemo, useState } from "react";
import { Box, Button, MenuItem, Switch, TextField, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, primaryButtonSx } from "./consoleTheme";
import { indiaDate } from "../../include/indiaDate";

const FALLBACK_EVENTS = [
  { id: "sale_register", label: "Sale register", hint: "When a store saves a sale" },
  { id: "loyalty_unlock", label: "Loyalty coupon unlock", hint: "When a lucky-draw coupon is issued to a customer" },
  { id: "draw_winner", label: "Draw winner", hint: "When a winner is picked" },
  { id: "store_register", label: "Store register", hint: "When a store is registered" },
];
const FALLBACK_CHANNELS = [
  { id: "email", label: "Email" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "sms", label: "SMS" },
  { id: "notification", label: "Notification" },
];
const FALLBACK_AUDIENCES = [
  { id: "customer", label: "Customer" },
  { id: "store_owner", label: "Store owner" },
];

const empty = {
  name: "",
  event: "sale_register",
  channel: "whatsapp",
  audience: "customer",
  language: "en_US",
  subject: "",
  body: "",
  whatsapp_template: "",
  whatsapp_vars: "store",
  whatsapp_image: "",
  whatsapp_header: "none",
  whatsapp_has_params: "0",
  status: "0",
};

function labelOf(list, id) {
  return list.find((item) => item.id === id)?.label || id;
}

export default function Communication() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [events, setEvents] = useState(FALLBACK_EVENTS);
  const [channels, setChannels] = useState(FALLBACK_CHANNELS);
  const [audiences, setAudiences] = useState(FALLBACK_AUDIENCES);
  const [whatsappReady, setWhatsappReady] = useState(false);
  const [smsReady, setSmsReady] = useState(false);

  const load = async () => {
    const result = await adminPost("COMM_LIST");
    if (result.status === "200") {
      setTemplates(result.data?.templates || []);
      setEvents(result.data?.events || FALLBACK_EVENTS);
      setChannels(result.data?.channels || FALLBACK_CHANNELS);
      setAudiences(result.data?.audiences || FALLBACK_AUDIENCES);
      setWhatsappReady(Boolean(result.data?.whatsapp_ready));
      setSmsReady(Boolean(result.data?.sms_ready));
    } else toast.error(result?.status_message || "Could not load communication");
  };

  useEffect(() => { load(); }, []);

  const toggle = async (row) => {
    const status = String(row.status) === "1" ? "0" : "1";
    const result = await adminPost("COMM_STATUS", { template_id: row.template_id, status });
    if (result.status === "200") {
      toast.success(result.data?.message || "Updated");
      setTemplates((prev) => prev.map((item) => (item.template_id === row.template_id ? result.data.template : item)));
    } else toast.error(result?.status_message || "Could not update template");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/console/communication/new")} sx={primaryButtonSx}>
          Add template
        </Button>
        <Button sx={{ textTransform: "none" }} onClick={() => navigate("/console/communication/logs")}>
          Communication logs
        </Button>
        <Typography sx={{ color: consoleColors.muted, fontSize: 13 }}>
          WhatsApp {whatsappReady ? "is connected" : "is not configured"} · SMS {smsReady ? "is connected" : "is not configured"}
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
        {events.map((event) => {
          const assigned = templates.filter((row) => row.event === event.id);
          return (
            <Box key={event.id} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2 }}>
              <Typography sx={{ fontWeight: 750 }}>{event.label}</Typography>
              <Typography sx={{ color: consoleColors.muted, fontSize: 13, mt: 0.4 }}>{event.hint}</Typography>
              <Box sx={{ mt: 1.2, display: "grid", gap: 0.7 }}>
                {assigned.length ? assigned.map((row) => (
                  <Box key={row.template_id} sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: 13.5 }}>
                      {row.name} · {labelOf(channels, row.channel)} · {labelOf(audiences, row.audience)}
                    </Typography>
                    <Switch size="small" checked={String(row.status) === "1"} onChange={() => toggle(row)} />
                  </Box>
                )) : (
                  <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>No template assigned</Typography>
                )}
              </Box>
              <Button size="small" sx={{ mt: 1, textTransform: "none" }} onClick={() => navigate(`/console/communication/new?event=${event.id}`)}>
                Assign template
              </Button>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 1.4, borderBottom: `1px solid ${consoleColors.line}` }}>
          <Typography sx={{ fontWeight: 750 }}>Templates</Typography>
        </Box>
        {templates.map((row) => (
          <Box key={row.template_id} sx={{ px: 2, py: 1.3, borderBottom: `1px solid ${consoleColors.line}`, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontWeight: 650 }}>{row.name}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>
                {labelOf(events, row.event)} · {labelOf(channels, row.channel)} · {labelOf(audiences, row.audience)}
                {row.date_added ? ` · ${indiaDate(row.date_added)}` : ""}
              </Typography>
            </Box>
            <Switch checked={String(row.status) === "1"} onChange={() => toggle(row)} />
            <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/communication/${row.template_id}/edit`)}>
              Edit
            </Button>
          </Box>
        ))}
        {!templates.length ? (
          <Typography sx={{ p: 2, color: consoleColors.muted }}>Add a template, tag an event, and enable it to auto-send.</Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export function CommunicationForm() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ ...empty, event: searchParams.get("event") || empty.event });
  const [events, setEvents] = useState(FALLBACK_EVENTS);
  const [channels, setChannels] = useState(FALLBACK_CHANNELS);
  const [audiences, setAudiences] = useState(FALLBACK_AUDIENCES);
  const [placeholders, setPlaceholders] = useState(["name", "store", "amount", "coupon", "mobile", "email", "draw", "points", "username", "owner"]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminPost("COMM_LIST").then((result) => {
      if (result.status === "200") {
        setEvents(result.data?.events || FALLBACK_EVENTS);
        setChannels(result.data?.channels || FALLBACK_CHANNELS);
        setAudiences(result.data?.audiences || FALLBACK_AUDIENCES);
        if (result.data?.placeholders) setPlaceholders(result.data.placeholders);
      }
    });
    if (!templateId) return;
    adminPost("COMM_DETAIL", { template_id: templateId }).then((result) => {
      if (result.status === "200" && result.data?.template) setForm({ ...empty, ...result.data.template });
      else toast.error(result?.status_message || "Template not found");
    });
  }, [templateId]);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const result = await adminPost("COMM_SAVE", form);
      if (result.status === "200") {
        toast.success(result.data?.message || "Template saved");
        navigate("/console/communication");
      } else toast.error(result?.status_message || "Could not save template");
    } finally {
      setSaving(false);
    }
  };

  const channel = form.channel;
  const helper = useMemo(
    () => `Placeholders: ${placeholders.map((item) => `{{${item}}}`).join(" ")}`,
    [placeholders]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 2, maxWidth: 880 }}>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontWeight: 750 }}>{templateId ? "Edit template" : "Add template"}</Typography>
        <Typography sx={{ color: consoleColors.muted, fontSize: 13.5 }}>
          Tag this template to an event and assign it to the customer or store owner. Enable it to auto-send.
        </Typography>
        <TextField label="Template name" value={form.name} onChange={set("name")} />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.4 }}>
          <TextField select label="Event" value={form.event} onChange={set("event")}>
            {events.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
          </TextField>
          <TextField select label="Channel" value={form.channel} onChange={set("channel")}>
            {channels.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
          </TextField>
          <TextField select label="Send to" value={form.audience} onChange={set("audience")}>
            {audiences.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
          </TextField>
          <TextField select label="Allow auto-send" value={form.status} onChange={set("status")}>
            <MenuItem value="0">Off</MenuItem>
            <MenuItem value="1">On</MenuItem>
          </TextField>
        </Box>
        {channel === "whatsapp" ? (
          <>
            <TextField label="WhatsApp template name" value={form.whatsapp_template} onChange={set("whatsapp_template")} helperText="Exact approved name on Flaxxa, for example cashi_coupon_unlock_ver1" />
            <TextField label="Language" value={form.language} onChange={set("language")} helperText="Must match the approved language. Use en_US" />
            <TextField select label="Header" value={form.whatsapp_header} onChange={set("whatsapp_header")}>
              <MenuItem value="none">No header</MenuItem>
              <MenuItem value="image">Image header — send image URL as {"{{1}}"}</MenuItem>
            </TextField>
            {form.whatsapp_header === "image" ? (
              <TextField
                label="Header image URL"
                value={form.whatsapp_image}
                onChange={set("whatsapp_image")}
                helperText="Public https PNG or JPEG. Required when the approved template header is an image."
              />
            ) : null}
            <TextField select label="Body" value={form.whatsapp_has_params} onChange={set("whatsapp_has_params")}>
              <MenuItem value="0">No body parameters — static text only</MenuItem>
              <MenuItem value="1">Dynamic body parameters — fill {"{{1}}"}, {"{{2}}"} from this event</MenuItem>
            </TextField>
            {form.whatsapp_has_params === "1" ? (
              <TextField
                label="Body variables in order"
                value={form.whatsapp_vars}
                onChange={set("whatsapp_vars")}
                helperText={`One key per body {{n}}, in order. cashi_coupon_unlock_ver1 is store. ${helper}`}
              />
            ) : (
              <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>
                No body component is sent. Use this when the approved template has no body {"{{1}}"}.
              </Typography>
            )}
            <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>
              Send only the header/body pieces that exist on the approved template. Extra or missing pieces return Invalid template.
            </Typography>
          </>
        ) : null}
        {channel === "email" ? <TextField label="Subject" value={form.subject} onChange={set("subject")} /> : null}
        {channel !== "whatsapp" ? (
          <TextField label="Message" value={form.body} onChange={set("body")} multiline minRows={6} helperText={helper} />
        ) : null}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" onClick={save} disabled={saving} sx={primaryButtonSx}>{saving ? "Saving..." : "Save template"}</Button>
          <Button sx={{ textTransform: "none" }} onClick={() => navigate("/console/communication")}>Cancel</Button>
        </Box>
      </Box>
    </Box>
  );
}
